const L = require('leaflet');
const m = require('mithril');
const edit = require('../layout/components/edit');
const polylabel = require('polylabel').default;

const LOCATION_CLASSES = ['Dashboard', 'Site', 'Building', 'Level', 'Room'];
let ROOM_CLASS_SET = new Set(['Room']);

function setRoomClassTree(tree) {
  const nextSet = new Set(['Room']);
  const categories = tree && Array.isArray(tree.categories) ? tree.categories : [];
  const walk = (nodes) => {
    (nodes || []).forEach(node => {
      if (!node || !node.name) return;
      nextSet.add(node.name);
      if (Array.isArray(node.children) && node.children.length) {
        walk(node.children);
      }
    });
  };
  walk(categories);
  ROOM_CLASS_SET = nextSet;
}

function isRoomClass(className) {
  if (!className || typeof className !== 'string') return false;
  if (ROOM_CLASS_SET.has(className)) return true;
  return /Room$/.test(className);
}

function isLocationClass(className) {
  return Boolean(className) && (LOCATION_CLASSES.includes(className) || isRoomClass(className));
}

const CONTEXTS = [
  {
    name: 'grandparent',
    zIndex: 1,
    func: async function () {
      let grandParent = this.location().path[this.location().path.length - 3];
      return grandParent ? [{in: grandParent}] : [];
    }
  },
  {
    name: 'parent',
    zIndex: 2,
    func: async function () {
      let grandParent = this.location().path[this.location().path.length - 2];
      return parent ? [{in: grandParent}] : [];
    }
  },
  {
    name: 'sibling',
    zIndex: 3,
    func: async function () {
      return [this.siblingFeatures()];
    }
  },
  {
    name: 'this',
    zIndex: 10,
    func: async function () {
      return [this.location()];
    }
  },
  {
    name: 'child',
    zIndex: 5,
    func: async function () {
      return this.childFeatures();
    }
  },
  {
    name: 'grandchild',
    zIndex: 6,
    func: async function () {
      return this.grandchildFeatures();
    }
  }
];

function getFeatureUid (feature) {
  let featureRid = feature.in ? feature.in['@rid'] : feature['@rid'];
  let featureUid = featureRid + "-" + feature.context;
  return featureUid;
};

function parseFeatureUid (featureUid) {
  let [rid, context] = featureUid.split('-');
  return {rid, context};
}
 
function getLocationLayers(locations) {
  if (!Array.isArray(locations)) return [];
  const geoJsons = {};
  const markers = [];

  locations.forEach(locationEdge => {
    let locationRecord;
    if (locationEdge.in) { locationRecord = locationEdge.in } else { locationRecord = locationEdge; }
    if (!isValidLocation(locationRecord)) return;

    const locationId = locationRecord['@rid'].toString().slice(1);
    const href = `/location/${locationId}`;

    const polygon = processGeoJSON(locationRecord, locationId);
    if (polygon && polygon.type === 'Polygon') {
      const coordinates = polygon ? polygon.getBounds().getCenter() : getLocationCoordinates(locationRecord);
    } else if (polygon && polygon.type === 'Point') {
      getLocationCoordinates(locationRecord);
    } else if (polygon && polygon.type === 'Polyline') {
      const coordinates = polygon ? polygon.getBounds().getCenter() : getLocationCoordinates(locationRecord);
    } else {
      const coordinates = getLocationCoordinates(locationRecord);
    }

    const childInfo = getChildInfo(locationRecord);
    const iconHtml = parentIconTemplate(locationRecord.name, href, childInfo.childClass, childInfo.childCount);

    const marker = createMarker(locationRecord, coordinates, iconHtml, href, locationRecord['@rid'].toString());
    marker.name = locationRecord.name;
    marker.type = 'Point';
    markers.push(marker);
  });

  return  [...Object.values(geoJsons), ...markers];
}

function isValidLocation(locationRecord) {
  return locationRecord && (locationRecord.geojson || (locationRecord.latitude && locationRecord.longitude));
}

function processGeoJSON(locationRecord, locationId, layerStyle) {
  layerStyle = layerStyle || {};
  if (!locationRecord.geojson) return null;
  try {
    const locationOptions = {
      style : (feature) => {
        const featureStyle = feature?.properties?.style;
        if (feature?.properties?.name == 'Bounding_Box') {
          if (edit.vm.editing()) {
            return {
              color: '#2196F3',
              weight: 1,
              opacity: 1,
              fillOpacity: 0,
            };
          } else {
            return {
              opacity: 0,
              fillOpacity: 0
            }
          }
        } else if (featureStyle) { 
          const styles = {};
          featureStyle.split(';').forEach((style) => {
            if (style.trim() === '') return;
            const [key, value] = style.split(':').map((s) => s.trim());
            styles[key] = value;
          });
          const leafletStyles =  mapSVGStylesToLeafletStyles(styles);
          return leafletStyles;
        } else {
          return layerStyle;
        }
      }
    }

    const recordGeoJSON = typeof locationRecord.geojson == 'string' ? JSON.parse(locationRecord.geojson) : locationRecord.geojson;
    const locationLayer = L.geoJSON(recordGeoJSON, locationOptions);
    locationLayer['@rid'] = locationRecord['@rid'].toString();
    locationLayer['@class'] = locationRecord['@class'];
    locationLayer.UID = locationRecord.UID;
    locationLayer.name = locationRecord.name;
    locationLayer.context = locationRecord.context;

    return locationLayer
  } catch (e) {
    console.error("Error parsing GeoJSON for location:", locationId, e);
    return null;
  }
}

function getChildInfo(locationRecord) {
  const childCount = locationRecord?.out_Contains?.length ?? locationRecord.out_isSiteOf?.length ?? locationRecord.out_hasPart?.length ?? 0;
  const childClass = LOCATION_CLASSES[LOCATION_CLASSES.indexOf(locationRecord['@class']) + 1] ?? 'Room';
  return { childCount, childClass };
}

function createMarker(locationRecord, coordinates, iconHtml, href) {
  return L.marker(coordinates, {
    icon: L.divIcon({
      pmIgnore: true,
      iconSize: 'auto',
      html: iconHtml,
      direction: 'center',
      className: 'map-divicon'
    }),
    title: href,
    '@rid': locationRecord['@rid'].toString(),
    UID:  getFeatureUid(locationRecord),
    type: 'Point'
  });
}

function parentIconTemplate(name, href, childClass, childCount = 0) {
  return `
    <div class="map-divicon">
      <a class="map-divicon-content fw4" href=#!${href}>
        ${name}
      </a>
    </div>
  `;
}

function getLocationCoordinates(location) {
  let latitude, longitude, geometry, geojsonObject;
  try {
    geojsonObject =  typeof location.geojson == 'string' ? JSON.parse(location.geojson) : location.geojson;
  } catch {
    console.error("Error parsing GeoJSON for location:", location['@rid'].toString());
    return null
  }
  if (geojsonObject && Array.isArray(geojsonObject.features) && geojsonObject.features.length > 0 && geojsonObject.features[0].geometry) {
    // For FeatureCollection, we're taking the first feature
    geometry = geojsonObject.features[0].geometry;
  } else if (geojsonObject && geojsonObject.geometry) {
    geometry = geojsonObject.geometry;
  }
  if (geometry) {
    switch (geometry.type) {
      case 'Point':
        // For Point, coordinates are an array of two numbers
        latitude = parseFloat(geometry.coordinates[1]);
        longitude = parseFloat(geometry.coordinates[0]);
        break;
      case 'Polygon':
        ({ latitude, longitude } = getGeometryBoundsCenter([geometry.coordinates]));
        break;
      case 'MultiPolygon':
        ({ latitude, longitude } = getGeometryBoundsCenter(geometry.coordinates));
        break;
      }
  } else {
    if (location.latitude && location.longitude) {
      latitude = parseFloat(location.latitude);
      longitude = parseFloat(location.longitude);
    }
  }

  return { latitude, longitude };
}

function getGeometryBoundsCenter(polygons) {
  let minLatitude = Infinity;
  let maxLatitude = -Infinity;
  let minLongitude = Infinity;
  let maxLongitude = -Infinity;

  const addCoordinate = (coordinate) => {
    if (!Array.isArray(coordinate) || coordinate.length < 2) return;
    const longitude = parseFloat(coordinate[0]);
    const latitude = parseFloat(coordinate[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    minLatitude = Math.min(minLatitude, latitude);
    maxLatitude = Math.max(maxLatitude, latitude);
    minLongitude = Math.min(minLongitude, longitude);
    maxLongitude = Math.max(maxLongitude, longitude);
  };

  const visit = (value) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number') {
      addCoordinate(value);
      return;
    }
    value.forEach(visit);
  };

  visit(polygons);
  if (!Number.isFinite(minLatitude) || !Number.isFinite(minLongitude)) {
    return { latitude: undefined, longitude: undefined };
  }

  return {
    latitude: (minLatitude + maxLatitude) / 2,
    longitude: (minLongitude + maxLongitude) / 2,
  };
}

function findSVGExtremePoints(svgElement) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  function updateExtremes(bbox) {
      minX = Math.min(minX, bbox.x);
      minY = Math.min(minY, bbox.y);
      maxX = Math.max(maxX, bbox.x + bbox.width);
      maxY = Math.max(maxY, bbox.y + bbox.height);
  }

  const elements = svgElement.querySelectorAll('path, circle, rect, polyline, polygon, ellipse');
  elements.forEach(el => {
      let bbox = el.getBBox();
      updateExtremes(bbox);
  });

  return { topLeft: { x: minX, y: minY }, bottomRight: { x: maxX, y: maxY } };

}

  // Function to create a GeoItem
  function createGeoItem(x, y, lat, lng) {
    let geoItem = document.createElementNS("http://www.nimrod.io", "GeoItem");
    geoItem.setAttribute("X", x);
    geoItem.setAttribute("Y", y);
    geoItem.setAttribute("Latitude", lat);
    geoItem.setAttribute("Longitude", lng);
    return geoItem;
  }

function calculateSVGBounds(svgElement, center, PPI=72, scaleFactor=8) {
  const lat = center.lat;
  const lng = center.lng;

  const extremePoints = findSVGExtremePoints(svgElement);
  // Apply scaling and calculate the bounds  

  // Convert pixel dimensions to feet and then to meters
  const widthPx = extremePoints.bottomRight.x - extremePoints.topLeft.x;
  const heightPx = extremePoints.bottomRight.y - extremePoints.topLeft.y;
  const widthMeters = (widthPx / PPI) * scaleFactor * 0.3048;
  const heightMeters = (heightPx / PPI) * scaleFactor * 0.3048;
  

  // Convert the dimensions from meters to degrees
  const latChange = heightMeters / 2 / 111320; // Half the height in degrees
  const lngChange = widthMeters / 2 / (111320 * Math.cos(Math.PI * lat / 180)); // Half the width in degrees

  // Calculate the northeast and southwest points
  const northEastLatLng = L.latLng(lat + latChange, lng + lngChange);
  const southWestLatLng = L.latLng(lat - latChange, lng - lngChange);

  // Create and return the bounds
  return L.latLngBounds(southWestLatLng, northEastLatLng);
}

function findSVGExtremePoints(svgElement) {
  let viewBox = svgElement.getAttribute('viewBox');
  let viewport;

  if (viewBox) {
      // Parse the viewBox value - it should be "minX minY width height"
      viewport = viewBox.split(' ').map(Number);
  } else {
      // Fallback to width and height if viewBox is not defined
      let width = svgElement.getAttribute('width');
      let height = svgElement.getAttribute('height');
      viewport = [0, 0, width, height];
  }

  // Calculate extreme points
  const topLeft = {x: viewport[0], y: viewport[1]};
  const bottomRight = {x: viewport[0] + viewport[2], y: viewport[1] + viewport[3]};
  const extremePoints = {topLeft, bottomRight};
  return extremePoints;
}


function addLocationMetaInfoToSVGElement (svgElement, center) {
  const { topLeft, bottomRight } = findSVGExtremePoints(svgElement);
  const bounds = calculateSVGBounds(svgElement, center);
  const northWest = bounds.getNorthWest();
  const southEast = bounds.getSouthEast();
  // Clear metainfo if it exists
  if (svgElement.querySelector("MetaInfo")) svgElement.querySelector("MetaInfo").remove();
  let metaInfo = document.createElementNS("http://www.nimrod.io", "MetaInfo");
  let geo = document.createElementNS("http://www.nimrod.io", "Geo");
  geo.appendChild(createGeoItem(topLeft.x, topLeft.y, northWest.lat, northWest.lng ));
  geo.appendChild(createGeoItem(bottomRight.x, bottomRight.y, southEast.lat, southEast.lng));

  // Append MetaInfo
  metaInfo.appendChild(geo);
  svgElement.appendChild(metaInfo);

  return SVGElement;
}

function createTempSvgElement(svgString) {
  const parser = new DOMParser();
  const svgElement = parser.parseFromString(svgString, "image/svg+xml").documentElement;
  // Temporarily append to the DOM
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "absolute";
  tempContainer.style.left = "-9999px";
  tempContainer.appendChild(svgElement);
  document.body.appendChild(tempContainer);
  return svgElement;
}

function mapSVGStylesToLeafletStyles(svgStyles) {
  const leafletStyles = {};

  // Map 'fill' to 'fillColor'
  if (svgStyles.fill && svgStyles.fill !== 'none') {
    leafletStyles.fillColor = svgStyles.fill;
    leafletStyles.fill = true; // Enable fill
  } else {
    leafletStyles.fill = false; // Disable fill if 'fill' is 'none' or not specified
  }

  // Map 'fill-opacity' to 'fillOpacity'
  if (svgStyles['fill-opacity']) {
    leafletStyles.fillOpacity = parseFloat(svgStyles['fill-opacity']);
  } else {
    leafletStyles.fillOpacity = 1.0; // Default to fully opaque if not specified
  }

  // Map 'stroke' to 'color'
  if (svgStyles.stroke && svgStyles.stroke !== 'none') {
    leafletStyles.color = svgStyles.stroke;
    leafletStyles.stroke = true; // Enable stroke
  } else {
    leafletStyles.stroke = false; // Disable stroke if 'stroke' is 'none' or not specified
  }

  // Map 'stroke-width' to 'weight'
  if (svgStyles['stroke-width']) {
    // Remove units like 'px' if present and parse as float
    leafletStyles.weight = parseFloat(svgStyles['stroke-width']);
  } else {
    leafletStyles.weight = 0; // Default to 0 if not specified
  }

  // Map 'stroke-opacity' to 'opacity'
  if (svgStyles['stroke-opacity']) {
    leafletStyles.opacity = parseFloat(svgStyles['stroke-opacity']);
  } else {
    leafletStyles.opacity = 1.0; // Default to fully opaque if not specified
  }

  // Map 'stroke-dasharray' to 'dashArray'
  if (svgStyles['stroke-dasharray']) {
    leafletStyles.dashArray = svgStyles['stroke-dasharray'];
  }

  // Map 'stroke-linecap' to 'lineCap'
  if (svgStyles['stroke-linecap']) {
    leafletStyles.lineCap = svgStyles['stroke-linecap'];
  }

  // Map 'stroke-linejoin' to 'lineJoin'
  if (svgStyles['stroke-linejoin']) {
    leafletStyles.lineJoin = svgStyles['stroke-linejoin'];
  }

  return leafletStyles;
}

function safePolylabel(coords, precision, ms = 200) {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(turf.centroid({ type: 'Polygon', coordinates: coords }).geometry.coordinates);
      }
    }, ms);
    const pole = polylabel(coords, precision);
    if (!done) {
      clearTimeout(timer);
      done = true;
      resolve(pole);
    }
  });
}


// Helper function to calculate the pole of inaccessibility (optimal label placement point) using polylabel
function getPoleOfInaccessibility(layer) {
  // Check if the layer is a feature group (L.geoJSON usually returns this)
  if (layer instanceof L.FeatureGroup || layer instanceof L.GeoJSON) {
    // Get the first polygon layer inside the feature group (you can extend this if you expect multiple polygons)
    for (let subLayer of layer.getLayers()) {
        if (subLayer?.feature?.geometry?.coordinates) {
          let coordinates;
          // Try for polygon, polylinestring, and multipolylinestring geometries
          Array.isArray(subLayer.feature.geometry.coordinates[0][0])
            ? coordinates = subLayer.feature.geometry.coordinates
            : coordinates = [[subLayer.feature.geometry.coordinates]];

          try {
            return safePolylabel(coordinates, 0.1); // 1.0 precision
          } catch (e) {
            console.error("Error calculating pole of inaccessibility for :", JSON.stringify(subLayer.feature.geometry, null, 2), e);
            return null;
          }
        }
    }
  } else if (layer instanceof L.Polygon) {
    try {
    } catch (e) {
      console.error("Error calculating pole of inaccessibility", JSON.stringify(layer.geometry, null, 2),e);
      return null;
    }
    return safePolylabel(layer.geometry.coordinates); // If it's a single polygon layer, get latlngs directly
  }
}

/**
 * Return the *child* edges for a location.
 *   • direct child locations
 *   • equipment whose in.path length === 2
 *
 * We reuse the same edge objects every call (no cloning), so Mithril’s diff
 * sees a stable feature set and stops the add/remove loop.
 */
function getChildLocations(location, equipmentLocations = []) {
  const children = [];

  // direct child locations
  if (location['@class'] === 'Site' && Array.isArray(location.out_isSiteOf)) {
    location.out_isSiteOf.forEach(e => children.push(e));
  } else if (
    (location['@class'] === 'Building' || location['@class'] === 'Level') &&
    Array.isArray(location.out_hasPart)
  ) {
    location.out_hasPart.forEach(e => children.push(e));
  } else if (
    location['@class'] === 'Dashboard' &&
    Array.isArray(location.out_Contains)
  ) {
    location.out_Contains
      .filter(e => e.in['@class'] === 'Site')
      .forEach(e => children.push(e));
  }
  // equipment two levels below
  equipmentLocations.forEach(edge => {
    if (edge?.in?.path?.length === 2) {
      if (!edge.context) edge.context = 'child';   // one‑time mutation
      children.push(edge);
    }
  });

  return children;
}

/**
 * Return the *grand‑child* edges for a location.
 *   • grandchild locations (buildings under sites on a dashboard)
 *   • equipment whose in.path length === 3
 */
function getGrandchildLocations(location, equipmentLocations = []) {
  const grands = [];

  // grandchild locations
  if (
    location['@class'] === 'Dashboard' &&
    Array.isArray(location.out_Contains)
  ) {
    location.out_Contains
      .filter(e => e.in['@class'] === 'Site')
      .forEach(siteEdge => {
        const site = siteEdge.in;
        if (Array.isArray(site.out_isSiteOf)) {
          site.out_isSiteOf.forEach(edge => {
            if (!edge.in.parent) edge.in.parent = site; // one‑time tag
            grands.push(edge);
          });
        }
      });
  }

  // equipment three levels below
  equipmentLocations.forEach(edge => {
    if (edge?.in?.path?.length === 3) {
      if (!edge.context) edge.context = 'grandchild';
      grands.push(edge);
    }
  });

  return grands;
}

module.exports = { 
  getLocationLayers, 
  getLocationCoordinates,
  createTempSvgElement,
  findSVGExtremePoints,
  addLocationMetaInfoToSVGElement,
  createGeoItem, 
  calculateSVGBounds, 
  processGeoJSON, 
  getChildInfo, 
  parentIconTemplate, 
  isValidLocation, 
  createMarker,
  CONTEXTS,
  LOCATION_CLASSES,
  setRoomClassTree,
  isRoomClass,
  isLocationClass,
  getFeatureUid,
  parseFeatureUid,
  getPoleOfInaccessibility,
  getChildLocations,
  getGrandchildLocations,
};
